import { BookmarkIcon, ResetIcon, TargetIcon } from '@radix-ui/react-icons'
import * as Slider from '@radix-ui/react-slider'
import ColorHandle from 'assets/color-handle.svg?react'
import RangeHandleLeft from 'assets/range-handle.svg?react'
import { TooltipWrapper } from 'components/TooltipWrapper'
import { useRolodexEdit } from 'features/fde'
import { useKestrel, useKestrelViewerContext } from 'features/kestrelViewer'
import { KestrelCommands } from 'features/kestrelViewer/types/KestrelCommands'
import debounce from 'lodash/debounce'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { KVDataLayersConfig, KVMeasurement, RolodexEntryPatchJson } from 'types'

const THERMAL_COMMAND_DEBOUNCE_MS = 500
const DEFAULT_OPACITY = 1.0
const OPACITY_STEP = 0.01
const MIN_CONTROL_POINTS = 2
const CONTROL_POINT_INCREMENT_FACTOR = 0.1
const OVERLAY_COLOR = '#808080'
const MEASUREMENT_CLEANUP_DELAY_MS = 200

interface ThermalControlPoint {
  temp_f: number
  color_rgba: [number, number, number, number]
}

/**
 * Clamps a value between a minimum and maximum range
 * @param value The value to clamp
 * @param min The minimum allowed value
 * @param max The maximum allowed value
 * @returns The clamped value
 */
const clamp = (value: number, min: number, max: number) => {
  return Math.max(Math.min(value, max), min)
}

const formatInchesToFeetAndInches = (totalInches: number): string => {
  const feet = Math.floor(totalInches / 12)
  const inches = totalInches % 12
  return `${feet}' ${inches.toFixed(1)}"`
}

const formatValueForLayer = (value: number, layerPrefix: string): string => {
  if (layerPrefix.startsWith('Elevation')) {
    return formatInchesToFeetAndInches(value)
  }
  return `${Math.round(value)}°F`
}

const RangeHandleRight = styled(RangeHandleLeft)`
  transform: rotate(180deg);
`

const SliderRoot = styled(Slider.Root)`
  position: relative;
  display: flex;
  flex-grow: 1;
  align-items: center;
  user-select: none;
  touch-action: none;
  height: 20px;
`

const SliderTrack = styled(Slider.Track)`
  position: relative;
  display: flex;
  flex-shrink: 1;
  flex-grow: 1;
  border-radius: 30px;
  height: 24px;
`

const SliderThumb = styled(Slider.Thumb)`
  display: block;
  width: max-content;
  background-color: transparent;
  z-index: 100;
  padding-top: 3px;

  &:focus {
    outline: none;
  }
`

const FilterSliderContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  position: absolute;
  z-index: 10;
`

const ColorSliderContainer = styled.div`
  position: relative;
  height: 0;
  transform: translateY(-1.5px);
  z-index: 50;
`

const ThermalGradientBackground = styled.div<{
  $gradientPoints: ThermalControlPoint[]
  $visibleRange: [number, number]
  $minTemp: number
  $maxTemp: number
}>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: 30px;
  background: ${({ $gradientPoints, $minTemp, $maxTemp }) => {
    if ($gradientPoints.length < 2) return 'transparent'

    const totalRange = $maxTemp - $minTemp

    const sortedPoints = [...$gradientPoints].sort(
      (a, b) => a.temp_f - b.temp_f
    )

    const firstPoint = sortedPoints[0]
    const lastPoint = sortedPoints[sortedPoints.length - 1]
    const [firstR, firstG, firstB] = firstPoint.color_rgba
    const [lastR, lastG, lastB] = lastPoint.color_rgba

    const gradientStops = []

    // Add starting color at 0%
    gradientStops.push(
      `rgb(${Math.round(firstR * 255)}, ${Math.round(firstG * 255)}, ${Math.round(firstB * 255)}) 0%`
    )

    // Add each control point at its calculated position within the range
    sortedPoints.forEach((point) => {
      const position = ((point.temp_f - $minTemp) / totalRange) * 100
      const [r, g, b] = point.color_rgba
      const color = `rgb(${Math.round(r * 255)}, ${Math.round(g * 255)}, ${Math.round(b * 255)})`
      gradientStops.push(`${color} ${position}%`)
    })

    // Add ending color at 100%
    gradientStops.push(
      `rgb(${Math.round(lastR * 255)}, ${Math.round(lastG * 255)}, ${Math.round(lastB * 255)}) 100%`
    )

    return `linear-gradient(to right, ${gradientStops.join(', ')})`
  }};
`

const VisibleRangeOverlay = styled.div<{
  $visibleRange: [number, number]
  $minTemp: number
  $maxTemp: number
}>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: 30px;
  pointer-events: none;
  background: ${({ $visibleRange, $minTemp, $maxTemp }) => {
    const [visibleMin, visibleMax] = $visibleRange
    const totalRange = $maxTemp - $minTemp
    const visibleStartPercent = ((visibleMin - $minTemp) / totalRange) * 100
    const visibleEndPercent = ((visibleMax - $minTemp) / totalRange) * 100

    const overlays = []

    // Add gray overlay before the visible range starts
    if (visibleStartPercent > 0) {
      overlays.push(`${OVERLAY_COLOR} 0%`)
      overlays.push(`${OVERLAY_COLOR} ${visibleStartPercent}%`)
      overlays.push(`transparent ${visibleStartPercent}%`)
    }

    if (visibleEndPercent < 100) {
      overlays.push(`transparent ${visibleEndPercent}%`)
      overlays.push(`${OVERLAY_COLOR} ${visibleEndPercent}%`)
      overlays.push(`${OVERLAY_COLOR} 100%`)
    }

    return overlays.length > 0
      ? `linear-gradient(to right, ${overlays.join(', ')})`
      : 'none'
  }};
`

const ViewSectionContainer = styled.div<{ $disabled?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 450px;
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
  pointer-events: ${({ $disabled }) => ($disabled ? 'none' : 'auto')};
  padding: 16px;
  background-color: ${({ theme }) => theme.forecast.background2};
  border-radius: 8px;
  margin-bottom: 12px;
`

const SliderLabel = styled.span`
  color: ${({ theme }) => theme.forecast.defaultText};
  font-size: 13px;
  font-weight: 500;
`

const SliderContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`

const StyledSliderRoot = styled(Slider.Root)`
  position: relative;
  display: flex;
  align-items: center;
  user-select: none;
  touch-action: none;
  width: 60%;
  height: 20px;
`

const StyledSliderTrack = styled(Slider.Track)`
  background-color: ${({ theme }) => theme.forecast.defaultButton};
  position: relative;
  flex-grow: 1;
  border-radius: 9999px;
  height: 4px;
`

const StyledSliderRange = styled(Slider.Range)`
  position: absolute;
  background-color: ${({ theme }) => theme.forecast.defaultButton};
  border-radius: 9999px;
  height: 100%;
`

const StyledSliderThumb = styled(Slider.Thumb)`
  display: block;
  width: 8px;
  height: 20px;
  background-color: ${({ theme }) => theme.forecast.activeText};
  border-radius: 9999px;
  outline: none;

  &:focus {
    outline: none;
  }
`

const OpacityInput = styled.input`
  display: block;
  width: 25%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  border-radius: 0.375rem;
  border: 1px solid ${({ theme }) => theme.forecast.background4};
  background-color: ${({ theme }) => theme.forecast.background1};
  color: ${({ theme }) => theme.forecast.defaultText};
  line-height: 1.5;
  padding: 4px 8px;
  font-size: 12px;

  &::placeholder {
    color: ${({ theme }) => theme.forecast.secondaryText};
  }

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.forecast.icon1};
  }
`

const HistogramSpacer = styled.div`
  height: 24px;
`

const Spacer18 = styled.div`
  height: 18px;
`

const HeaderContainer = styled.div`
  height: 40px;
  padding-top: 4px;
`

const RelativeContainer = styled.div`
  position: relative;
`

const AbsoluteSliderRoot = styled(SliderRoot)`
  position: absolute;
  width: 100%;
`

const ThumbEResize = styled(SliderThumb)`
  cursor: e-resize;
`

const ThumbWResize = styled(SliderThumb)`
  cursor: w-resize;
`

const ColorThumb = styled(SliderThumb)<{ $color: string }>`
  color: ${({ $color }) => $color};
  transform: translateY(-30%);
`

const ControlPointIndicator = styled.div<{ $position: number; $color: string }>`
  position: absolute;
  top: -25px;
  left: ${({ $position }) => $position}%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 4px;
  background: ${({ theme }) => theme.forecast.background3};
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 10px;
  color: ${({ theme }) => theme.forecast.activeText};
  border: 1px solid ${({ $color }) => $color};
  z-index: 1000;
`

const ControlPointDeleteButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.forecast.secondaryText};
  cursor: pointer;
  padding: 0;
  width: 12px;
  height: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 2px;
  font-size: 10px;

  &:hover {
    background-color: ${({ theme }) => theme.forecast.background4};
    color: ${({ theme }) => theme.forecast.activeText};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const AddControlPointButton = styled.button`
  position: absolute;
  top: -20px;
  right: 0;
  background: ${({ theme }) => theme.forecast.background3};
  border: 1px solid ${({ theme }) => theme.forecast.background4};
  color: ${({ theme }) => theme.forecast.defaultText};
  cursor: pointer;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
  z-index: 1000;

  &:hover {
    background-color: ${({ theme }) => theme.forecast.background4};
    color: ${({ theme }) => theme.forecast.activeText};
  }
`

const TemperatureRangeLabels = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 8px;
  font-size: 11px;
  color: ${({ theme }) => theme.forecast.secondaryText};
`

const DisabledMessage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  color: ${({ theme }) => theme.forecast.secondaryText};
  font-style: italic;
`

const VisualDisabledMessage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px 20px;
  color: ${({ theme }) => theme.forecast.secondaryText};
  font-style: italic;
`

const LoadingMessage = styled.div`
  padding: 20px;
  color: ${({ theme }) => theme.forecast.secondaryText};
`

const SaveLayerInputContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 12px;
`

const SaveLayerInput = styled.input`
  display: block;
  width: 100%;
  border-radius: 0.375rem;
  border: 1px solid ${({ theme }) => theme.forecast.background4};
  background-color: ${({ theme }) => theme.forecast.background1};
  color: ${({ theme }) => theme.forecast.defaultText};
  line-height: 1.5;
  padding: 6px 8px;
  font-size: 13px;

  &::placeholder {
    color: ${({ theme }) => theme.forecast.secondaryText};
  }

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.forecast.icon1};
  }
`

const SaveLayerActions = styled.div`
  display: flex;
  gap: 8px;
`

const CancelButton = styled.button`
  background-color: ${({ theme }) => theme.forecast.background3};
  color: ${({ theme }) => theme.forecast.defaultText};
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  flex: 1;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${({ theme }) => theme.forecast.background4};
  }
`

const ConfirmSaveButton = styled.button`
  background-color: ${({ theme }) => theme.forecast.activeButton};
  color: ${({ theme }) => theme.forecast.activeText};
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  flex: 2;
  transition: all 0.2s ease;

  &:hover {
    filter: brightness(1.1);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const ButtonsRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  margin-top: 8px;
`

const IconButton = styled.button<{ $active?: boolean }>`
  background-color: ${({ theme, $active }) =>
    $active ? theme.forecast.activeButton : theme.forecast.background3};
  color: ${({ theme, $active }) =>
    $active ? theme.forecast.activeText : theme.forecast.defaultText};
  border: 1px solid
    ${({ theme, $active }) =>
      $active ? theme.forecast.activeButton : theme.forecast.background4};
  border-radius: 6px;
  padding: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  min-width: 36px;
  height: 36px;

  &:hover:not(:disabled) {
    background-color: ${({ theme, $active }) =>
      $active ? theme.forecast.activeButton : theme.forecast.background4};
    filter: ${({ $active }) => ($active ? 'brightness(1.1)' : 'none')};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    width: 18px;
    height: 18px;
  }
`

const SaveButton = styled(IconButton)`
  flex: 1;
  gap: 6px;
  font-size: 13px;
  font-weight: 500;
`

const ProbeStatusMessage = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.forecast.secondaryText};
  padding: 6px 10px;
  text-align: center;
  background-color: ${({ theme }) => theme.forecast.background3};
  border-radius: 4px;
  margin-top: 4px;
`

/**
 * Generates default thermal gradient control points for a temperature range
 * Creates a 5-point gradient from cold (purple) to hot (white) following
 * standard thermal imaging color conventions
 * @param minTemp The minimum temperature of the range
 * @param maxTemp The maximum temperature of the range
 * @returns Array of thermal control points with temperatures and RGBA colors
 */
const generateDefaultControlPoints = (
  minTemp: number,
  maxTemp: number
): ThermalControlPoint[] => {
  const range = maxTemp - minTemp
  // Original proportions from -100°F to 300°F range: -16, 133, 186, 251, 300
  // Proportions: 21%, 58.25%, 71.5%, 87.75%, 100%
  return [
    { temp_f: minTemp + range * 0.21, color_rgba: [0.2, 0.0, 0.4, 1.0] }, // 21% - Dark purple (cold)
    { temp_f: minTemp + range * 0.5825, color_rgba: [0.8, 0.0, 0.2, 1.0] }, // 58.25% - Dark red
    { temp_f: minTemp + range * 0.715, color_rgba: [1.0, 0.1, 0.0, 1.0] }, // 71.5% - Bright red
    { temp_f: minTemp + range * 0.8775, color_rgba: [1.0, 0.85, 0.0, 1.0] }, // 87.75% - Yellow
    { temp_f: minTemp + range * 1.0, color_rgba: [1.0, 1.0, 0.95, 1.0] }, // 100% - White (hot)
  ]
}

/**
 * Converts RGBA color values (0-1 range) to hex color string
 * @param r Red component (0-1)
 * @param g Green component (0-1)
 * @param b Blue component (0-1)
 * @returns Hex color string (e.g., "#ff0000")
 */
const rgbaToHex = (r: number, g: number, b: number): string => {
  const toHex = (n: number) =>
    Math.round(n * 255)
      .toString(16)
      .padStart(2, '0')
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`
}

/**
 * Remaps control points based on two probed temperatures
 * Creates control points that extend 10% beyond the probed range
 * Example: if probed temps are 90 and 130 (range=40), creates points at:
 * 86 (90-4), 90, 110, 130, 134 (130+4)
 * @param points Current control points for color reference
 * @param temp1 First probed temperature
 * @param temp2 Second probed temperature
 * @param minTemp Minimum temperature of the LUT for clamping
 * @param maxTemp Maximum temperature of the LUT for clamping
 * @returns New control points at specific temperatures
 */
const remapControlPointsByProbe = (
  points: ThermalControlPoint[],
  temp1: number,
  temp2: number,
  minTemp: number,
  maxTemp: number
): ThermalControlPoint[] => {
  const [lowTemp, highTemp] = temp1 < temp2 ? [temp1, temp2] : [temp2, temp1]
  const probedRange = highTemp - lowTemp

  if (probedRange === 0) {
    return points
  }

  const sortedPoints = [...points].sort((a, b) => a.temp_f - b.temp_f)

  const padding = probedRange * 0.1
  const extendedLowTemp = lowTemp - padding
  const extendedHighTemp = highTemp + padding

  const quarterTemp = lowTemp + probedRange * 0.25
  const midTemp = lowTemp + probedRange * 0.5
  const threeQuarterTemp = lowTemp + probedRange * 0.75

  const newControlPoints: ThermalControlPoint[] = [
    {
      temp_f: clamp(extendedLowTemp, minTemp, maxTemp),
      color_rgba: sortedPoints[0].color_rgba,
    },
    {
      temp_f: clamp(lowTemp, minTemp, maxTemp),
      color_rgba: sortedPoints[Math.min(1, sortedPoints.length - 1)].color_rgba,
    },
    {
      temp_f: clamp(quarterTemp, minTemp, maxTemp),
      color_rgba:
        sortedPoints[Math.floor(sortedPoints.length * 0.33)].color_rgba,
    },
    {
      temp_f: clamp(midTemp, minTemp, maxTemp),
      color_rgba: sortedPoints[Math.floor(sortedPoints.length / 2)].color_rgba,
    },
    {
      temp_f: clamp(threeQuarterTemp, minTemp, maxTemp),
      color_rgba:
        sortedPoints[Math.floor(sortedPoints.length * 0.67)].color_rgba,
    },
    {
      temp_f: clamp(highTemp, minTemp, maxTemp),
      color_rgba: sortedPoints[Math.max(sortedPoints.length - 2, 0)].color_rgba,
    },
    {
      temp_f: clamp(extendedHighTemp, minTemp, maxTemp),
      color_rgba: sortedPoints[sortedPoints.length - 1].color_rgba,
    },
  ]

  return newControlPoints
}

/**
 * Thermal gradient control panel for rooftop data layers
 * Handles RGB, Visual, and Raw thermal data layer controls
 */
export const ThermalGradientPanel: React.FC = () => {
  const {
    sendCommand,
    activeLayer,
    setActiveLayer,
    dataLayersConfig,
    currentTemperature,
    setCurrentTemperature,
    selectedMode,
    setSelectedMode,
    measurementType,
    setMeasurementType,
  } = useKestrelViewerContext()
  const { kestrelModel, setKestrelModel, measurements, setMeasurements } =
    useKestrel()
  const { rolodexPatch } = useRolodexEdit()

  const [layerControlPointsCache, setLayerControlPointsCache] = useState<
    Record<string, ThermalControlPoint[]>
  >({})
  const [layerOpacityCache, setLayerOpacityCache] = useState<
    Record<string, number>
  >({})
  const [layerVisibleRangeCache, setLayerVisibleRangeCache] = useState<
    Record<string, [number, number]>
  >({})
  const [controlPoints, setControlPoints] = useState<ThermalControlPoint[]>([])
  const [visibleRange, setVisibleRange] = useState<[number, number]>([0, 100])
  const [opacity, setOpacity] = useState(DEFAULT_OPACITY)
  const [currentLayerKey, setCurrentLayerKey] = useState<string>('')
  const [showSaveUI, setShowSaveUI] = useState(false)
  const [customLayerName, setCustomLayerName] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [gradientByProbeActive, setGradientByProbeActive] = useState(false)
  const [probedTemperatures, setProbedTemperatures] = useState<number[]>([])
  const [gradientMeasurementId, setGradientMeasurementId] = useState<
    string | null
  >(null)
  const [previousMode, setPreviousMode] = useState<string>('orbit')
  const [previousMeasurementType, setPreviousMeasurementType] =
    useState<string>('straightLine')

  const isRgbLayer = activeLayer.type === 'rgb'
  const isVisualLayer = activeLayer.type === 'visual'
  const isRawLayer = activeLayer.type === 'raw'

  const decodeMinTemp = activeLayer.decode_min_f ?? 0
  const decodeMaxTemp = activeLayer.decode_max_f ?? 100

  if (decodeMaxTemp <= decodeMinTemp) {
    console.warn('Invalid temperature range detected:', {
      decodeMinTemp,
      decodeMaxTemp,
    })
  }

  const gradientMinTemp = decodeMinTemp
  const gradientMaxTemp = decodeMaxTemp

  const debouncedSendCommand = useMemo(
    () =>
      debounce((command: object) => {
        sendCommand(command)
      }, THERMAL_COMMAND_DEBOUNCE_MS),
    [sendCommand]
  )

  useEffect(() => {
    debouncedSendCommand.cancel()
  }, [activeLayer.type, activeLayer.prefix, debouncedSendCommand])

  const sendThermalCommand = useCallback(
    (
      points: ThermalControlPoint[],
      range: [number, number],
      opacityValue: number
    ) => {
      // Raw layers require at least 2 control points for gradient
      if (activeLayer.type === 'raw' && points.length < MIN_CONTROL_POINTS) {
        return
      }

      const sortedPoints = [...points].sort((a, b) => a.temp_f - b.temp_f)

      if (activeLayer.type === 'raw') {
        const command = {
          command: KestrelCommands.SHOW_LAYER_RAW,
          prefix: activeLayer.prefix,
          decode_min_f: decodeMinTemp,
          decode_max_f: decodeMaxTemp,
          visible_min_f: range[0],
          visible_max_f: range[1],
          opacity: opacityValue,
          gradient: {
            control_points: sortedPoints,
          },
        }

        debouncedSendCommand(command)

        setActiveLayer((prev) => ({
          ...prev,
          visible_min_f: range[0],
          visible_max_f: range[1],
          opacity: opacityValue,
        }))
      } else if (activeLayer.type === 'visual') {
        const command = {
          command: KestrelCommands.SHOW_LAYER_VISUAL,
          prefix: activeLayer.prefix,
          opacity: opacityValue,
        }

        debouncedSendCommand(command)

        setActiveLayer((prev) => ({
          ...prev,
          opacity: opacityValue,
        }))
      }
      // RGB layers don't need thermal commands
    },
    [
      debouncedSendCommand,
      activeLayer.prefix,
      activeLayer.type,
      decodeMinTemp,
      decodeMaxTemp,
      setActiveLayer,
    ]
  )

  // Generate unique key for layer caching based on layer properties and temperature range
  const layerKey = `${activeLayer.prefix}_${activeLayer.type}_${decodeMinTemp}_${decodeMaxTemp}`

  useEffect(() => {
    if (currentLayerKey === layerKey) return

    if (currentLayerKey && currentLayerKey !== layerKey) {
      if (controlPoints.length >= MIN_CONTROL_POINTS) {
        setLayerControlPointsCache((prev) => ({
          ...prev,
          [currentLayerKey]: controlPoints,
        }))
        setLayerVisibleRangeCache((prev) => ({
          ...prev,
          [currentLayerKey]: visibleRange,
        }))
      }
      setLayerOpacityCache((prev) => ({
        ...prev,
        [currentLayerKey]: opacity,
      }))
    }

    if (isRawLayer) {
      // For custom layers, prioritize saved gradient over cache
      if (
        activeLayer.isCustom &&
        activeLayer.gradient?.control_points &&
        activeLayer.gradient.control_points.length >= MIN_CONTROL_POINTS
      ) {
        setControlPoints(
          activeLayer.gradient.control_points.map((point) => ({
            temp_f: point.temp_f,
            color_rgba: point.color_rgba as [number, number, number, number],
          }))
        )
      } else {
        const cachedControlPoints = layerControlPointsCache[layerKey]
        if (
          cachedControlPoints &&
          cachedControlPoints.length >= MIN_CONTROL_POINTS
        ) {
          setControlPoints(cachedControlPoints)
        } else if (
          activeLayer.gradient?.control_points &&
          activeLayer.gradient.control_points.length >= MIN_CONTROL_POINTS
        ) {
          setControlPoints(
            activeLayer.gradient.control_points.map((point) => ({
              temp_f: point.temp_f,
              color_rgba: point.color_rgba as [number, number, number, number],
            }))
          )
        } else {
          setControlPoints(
            generateDefaultControlPoints(decodeMinTemp, decodeMaxTemp)
          )
        }
      }

      // For custom layers, prioritize saved visible range over cache
      if (
        activeLayer.isCustom &&
        activeLayer.visible_min_f !== undefined &&
        activeLayer.visible_max_f !== undefined
      ) {
        setVisibleRange([activeLayer.visible_min_f, activeLayer.visible_max_f])
      } else {
        const cachedVisibleRange = layerVisibleRangeCache[layerKey]
        if (cachedVisibleRange) {
          setVisibleRange(cachedVisibleRange)
        } else if (
          activeLayer.visible_min_f !== undefined &&
          activeLayer.visible_max_f !== undefined
        ) {
          setVisibleRange([
            activeLayer.visible_min_f,
            activeLayer.visible_max_f,
          ])
        } else {
          setVisibleRange([decodeMinTemp, decodeMaxTemp])
        }
      }
    } else if (isVisualLayer) {
      setControlPoints([])
      setVisibleRange([decodeMinTemp, decodeMaxTemp])
    }

    // For opacity: prioritize saved layer opacity over cache for custom layers
    // For visual layers, always reset to 1.0
    if (isVisualLayer) {
      setOpacity(1.0)
    } else if (activeLayer.opacity !== undefined && activeLayer.isCustom) {
      // Custom layers: use saved opacity from layer config (don't use cache)
      setOpacity(activeLayer.opacity)
    } else {
      // Non-custom layers: use cache if available, otherwise use activeLayer or default
      const cachedOpacity = layerOpacityCache[layerKey]
      if (cachedOpacity !== undefined) {
        setOpacity(cachedOpacity)
      } else if (activeLayer.opacity !== undefined) {
        setOpacity(activeLayer.opacity)
      } else {
        setOpacity(DEFAULT_OPACITY)
      }
    }

    setCurrentLayerKey(layerKey)
  }, [
    layerKey,
    currentLayerKey,
    activeLayer.gradient?.control_points,
    activeLayer.visible_min_f,
    activeLayer.visible_max_f,
    activeLayer.opacity,
    activeLayer.isCustom,
    decodeMinTemp,
    decodeMaxTemp,
    isRawLayer,
    isVisualLayer,
    layerControlPointsCache,
    layerOpacityCache,
    layerVisibleRangeCache,
    controlPoints,
    visibleRange,
    opacity,
  ])

  useEffect(() => {
    if (currentLayerKey !== layerKey) return

    if (isRawLayer && controlPoints.length >= MIN_CONTROL_POINTS) {
      setLayerControlPointsCache((prev) => ({
        ...prev,
        [layerKey]: controlPoints,
      }))
    }

    if (isRawLayer) {
      setLayerVisibleRangeCache((prev) => ({
        ...prev,
        [layerKey]: visibleRange,
      }))
    }

    if (isRawLayer || isVisualLayer) {
      setLayerOpacityCache((prev) => ({
        ...prev,
        [layerKey]: opacity,
      }))
    }
  }, [
    controlPoints,
    visibleRange,
    opacity,
    currentLayerKey,
    layerKey,
    isRawLayer,
    isVisualLayer,
  ])

  // Send thermal commands when layer properties change
  useEffect(() => {
    if (currentLayerKey !== layerKey) return

    if (isRawLayer) {
      if (controlPoints.length >= MIN_CONTROL_POINTS) {
        sendThermalCommand(controlPoints, visibleRange, opacity)
      }
    } else if (isVisualLayer) {
      sendThermalCommand([], visibleRange, opacity)
    }
  }, [
    controlPoints,
    visibleRange,
    opacity,
    sendThermalCommand,
    isRawLayer,
    isVisualLayer,
    currentLayerKey,
    layerKey,
  ])

  useEffect(() => {
    if (!gradientByProbeActive || probedTemperatures.length !== 1) return
    if (gradientMeasurementId) return
    if (!measurements || measurements.length === 0) return

    const latestMeasurement = measurements[measurements.length - 1]
    if (latestMeasurement?.id && latestMeasurement.segments?.length > 0) {
      setGradientMeasurementId(latestMeasurement.id)
      sendCommand({ command: KestrelCommands.GET_CURSOR_DATA })
    }
  }, [
    gradientByProbeActive,
    probedTemperatures.length,
    measurements,
    gradientMeasurementId,
    sendCommand,
  ])

  useEffect(() => {
    if (!gradientByProbeActive || !currentTemperature) return

    const newTemp = currentTemperature.temperature

    if (probedTemperatures.length === 0) {
      setProbedTemperatures([newTemp])
      setCurrentTemperature(null)
      sendCommand({ command: KestrelCommands.SET_MODE, mode: 'measure' })
      sendCommand({
        command: KestrelCommands.TOGGLE_MEASUREMENT_TYPE,
        measurementType: 'straightLine',
      })
      setSelectedMode('measure')
      setMeasurementType('straightLine')
    } else if (probedTemperatures.length === 1) {
      const remappedPoints = remapControlPointsByProbe(
        controlPoints,
        probedTemperatures[0],
        newTemp,
        gradientMinTemp,
        gradientMaxTemp
      )
      setControlPoints(remappedPoints)
      setProbedTemperatures([])
      setGradientByProbeActive(false)
      setCurrentTemperature(null)

      const measurementIdToDelete = gradientMeasurementId

      sendCommand({ command: KestrelCommands.CLEAR_CURRENT_SEGMENT })

      if (measurementIdToDelete && measurements) {
        setTimeout(() => {
          const filteredMeasurements = measurements.filter(
            (m) => m.id !== measurementIdToDelete
          )
          setMeasurements(filteredMeasurements)

          sendCommand({
            command: KestrelCommands.DELETE_MEASUREMENT,
            measurementId: measurementIdToDelete,
          })

          if (kestrelModel?.id) {
            const patchData: RolodexEntryPatchJson = {
              id: kestrelModel.id,
              data: {
                measurements:
                  filteredMeasurements?.map((m) => KVMeasurement.toJSON(m)) ??
                  [],
              },
            }
            rolodexPatch([patchData])
          }
        }, MEASUREMENT_CLEANUP_DELAY_MS)
        setGradientMeasurementId(null)
      }

      sendCommand({ command: KestrelCommands.SET_MODE, mode: previousMode })
      sendCommand({
        command: KestrelCommands.SET_POINTER_MODE,
        createAnnotationOnClick: true,
      })
      setSelectedMode(previousMode)
      if (previousMode === 'measure') {
        setMeasurementType(previousMeasurementType)
      }
    }
  }, [
    currentTemperature,
    gradientByProbeActive,
    probedTemperatures,
    controlPoints,
    gradientMinTemp,
    gradientMaxTemp,
    setCurrentTemperature,
    selectedMode,
    setSelectedMode,
    sendCommand,
    previousMode,
    previousMeasurementType,
    setMeasurementType,
    gradientMeasurementId,
    measurements,
    setMeasurements,
    kestrelModel,
    rolodexPatch,
  ])

  const clampVisibleRange = useCallback(
    (range: [number, number]): [number, number] => {
      const clampedMin = Math.max(range[0], decodeMinTemp)
      const clampedMax = Math.min(range[1], decodeMaxTemp)
      return [clampedMin, clampedMax]
    },
    [decodeMinTemp, decodeMaxTemp]
  )

  const addControlPoint = () => {
    const lastPoint = controlPoints[controlPoints.length - 1]
    const range = gradientMaxTemp - gradientMinTemp
    const increment = range * CONTROL_POINT_INCREMENT_FACTOR
    const newTemp = lastPoint
      ? Math.min(lastPoint.temp_f + increment, gradientMaxTemp)
      : gradientMinTemp + increment
    const newPoint: ThermalControlPoint = {
      temp_f: newTemp,
      color_rgba: [1.0, 1.0, 1.0, 1.0],
    }
    setControlPoints([...controlPoints, newPoint])
  }

  const removeControlPoint = (index: number) => {
    if (controlPoints.length > MIN_CONTROL_POINTS) {
      const newPoints = controlPoints.filter((_, i) => i !== index)
      setControlPoints(newPoints)
    }
  }

  const generateUniqueCustomId = useCallback(() => {
    if (!dataLayersConfig) return 'custom-1'

    const existingCustomIds = dataLayersConfig.layers
      .filter((l) => l.customId)
      .map((l) => l.customId)
    let counter = 1
    while (existingCustomIds.includes(`custom-${counter}`)) {
      counter++
    }
    return `custom-${counter}`
  }, [dataLayersConfig])

  const handleResetGradient = useCallback(() => {
    const defaultPoints = generateDefaultControlPoints(
      gradientMinTemp,
      gradientMaxTemp
    )
    setControlPoints(defaultPoints)
  }, [gradientMinTemp, gradientMaxTemp])

  const handleGradientByProbe = useCallback(() => {
    if (gradientByProbeActive) {
      setGradientByProbeActive(false)
      setProbedTemperatures([])
      setCurrentTemperature(null)
      setGradientMeasurementId(null)

      if (gradientMeasurementId) {
        sendCommand({
          command: KestrelCommands.DELETE_MEASUREMENT,
          measurementId: gradientMeasurementId,
        })
      }

      sendCommand({ command: KestrelCommands.SET_MODE, mode: previousMode })
      sendCommand({
        command: KestrelCommands.SET_POINTER_MODE,
        createAnnotationOnClick: true,
      })
      setSelectedMode(previousMode)
      if (previousMode === 'measure') {
        setMeasurementType(previousMeasurementType)
      }
    } else {
      setPreviousMode(selectedMode)
      setPreviousMeasurementType(measurementType)
      setGradientByProbeActive(true)
      setProbedTemperatures([])
      setCurrentTemperature(null)
      setGradientMeasurementId(null)

      sendCommand({ command: KestrelCommands.SET_MODE, mode: 'cursor' })
      sendCommand({
        command: KestrelCommands.SET_POINTER_MODE,
        createAnnotationOnClick: false,
      })
      setSelectedMode('temperature')
    }
  }, [
    gradientByProbeActive,
    selectedMode,
    setSelectedMode,
    setCurrentTemperature,
    sendCommand,
    previousMode,
    previousMeasurementType,
    setMeasurementType,
    measurementType,
    gradientMeasurementId,
  ])

  const handleSaveLayer = useCallback(async () => {
    if (!kestrelModel?.id || !dataLayersConfig || !customLayerName.trim()) {
      return
    }

    setIsSaving(true)

    try {
      const customId = generateUniqueCustomId()

      // Ensure gradient control points span the full visible range
      const sortedPoints = [...controlPoints].sort(
        (a, b) => a.temp_f - b.temp_f
      )
      const firstPoint = sortedPoints[0]
      const lastPoint = sortedPoints[sortedPoints.length - 1]

      // Extend gradient to cover visible range if needed
      const extendedPoints = [...sortedPoints]

      // Add point at visible_min if first point is above it
      if (firstPoint.temp_f > visibleRange[0]) {
        extendedPoints.unshift({
          temp_f: visibleRange[0],
          color_rgba: firstPoint.color_rgba,
        })
      }

      // Add point at visible_max if last point is below it
      if (lastPoint.temp_f < visibleRange[1]) {
        extendedPoints.push({
          temp_f: visibleRange[1],
          color_rgba: lastPoint.color_rgba,
        })
      }

      const newLayer = {
        prefix: activeLayer.prefix, // Use source layer's prefix
        name: customLayerName.trim(),
        type: 'raw' as const,
        decodeRange: {
          min_f: decodeMinTemp,
          max_f: decodeMaxTemp,
        },
        isCustom: true,
        customId: customId, // Unique identifier for UI
        savedVisibleRange: {
          min_f: visibleRange[0],
          max_f: visibleRange[1],
        },
        savedGradient: {
          control_points: extendedPoints,
        },
        savedOpacity: opacity,
        description: 'Custom filtered layer',
      }

      const updatedConfig = new KVDataLayersConfig()
      updatedConfig.layers = [...dataLayersConfig.layers, newLayer]
      updatedConfig.layerSwitchingEnabled =
        dataLayersConfig.layerSwitchingEnabled

      const patchData: RolodexEntryPatchJson = {
        id: kestrelModel.id,
        data: {
          dataLayersConfig: KVDataLayersConfig.toJSON(updatedConfig),
        },
      }

      await rolodexPatch([patchData])

      // Update local kestrelModel state
      if (kestrelModel) {
        setKestrelModel({
          ...kestrelModel,
          data: {
            ...kestrelModel.data,
            dataLayersConfig: updatedConfig,
          },
        })
      }

      setShowSaveUI(false)
      setCustomLayerName('')
    } catch (error) {
      console.error('Failed to save custom layer:', error)
    } finally {
      setIsSaving(false)
    }
  }, [
    kestrelModel,
    dataLayersConfig,
    customLayerName,
    generateUniqueCustomId,
    activeLayer.prefix,
    decodeMinTemp,
    decodeMaxTemp,
    visibleRange,
    controlPoints,
    opacity,
    rolodexPatch,
    setKestrelModel,
  ])

  if (isRgbLayer) {
    return (
      <ViewSectionContainer data-testid='thermal-gradient-panel'>
        <Spacer18 />
        <HeaderContainer>
          <DisabledMessage>Not available for RGB data layer</DisabledMessage>
        </HeaderContainer>
      </ViewSectionContainer>
    )
  }

  if (isVisualLayer) {
    return (
      <ViewSectionContainer data-testid='thermal-gradient-panel'>
        <Spacer18 />
        <HeaderContainer>
          <VisualDisabledMessage>
            Gradient controls not available for VISUAL data layer
          </VisualDisabledMessage>
        </HeaderContainer>

        <SliderContainer>
          <SliderLabel>Opacity</SliderLabel>
          <StyledSliderRoot
            value={[opacity]}
            onValueChange={(values) => setOpacity(values[0])}
            min={0}
            max={1}
            step={OPACITY_STEP}>
            <StyledSliderTrack>
              <StyledSliderRange />
            </StyledSliderTrack>
            <StyledSliderThumb />
          </StyledSliderRoot>
          <OpacityInput
            value={opacity.toFixed(2)}
            onChange={(e) => setOpacity(parseFloat(e.target.value) || 0)}
            type='number'
            min={0}
            max={1}
            step={OPACITY_STEP}
          />
        </SliderContainer>
      </ViewSectionContainer>
    )
  }

  // Raw layers require control points for gradient display
  if (isRawLayer && controlPoints.length < MIN_CONTROL_POINTS) {
    return (
      <ViewSectionContainer data-testid='thermal-gradient-panel'>
        <Spacer18 />
        <HeaderContainer>
          <LoadingMessage>Loading gradient controls...</LoadingMessage>
        </HeaderContainer>
      </ViewSectionContainer>
    )
  }

  return (
    <ViewSectionContainer data-testid='thermal-gradient-panel'>
      <Spacer18 />

      <HeaderContainer>
        <RelativeContainer>
          <FilterSliderContainer>
            <SliderRoot
              min={decodeMinTemp}
              max={decodeMaxTemp}
              value={[visibleRange[0], visibleRange[1]]}
              step={1}
              onValueChange={(value) => {
                // Radix UI returns number[] but we need a tuple for our range type
                const clampedRange = clampVisibleRange(
                  value as [number, number]
                )
                setVisibleRange(clampedRange)
              }}>
              <SliderTrack>
                <ThermalGradientBackground
                  $gradientPoints={controlPoints}
                  $visibleRange={visibleRange}
                  $minTemp={gradientMinTemp}
                  $maxTemp={gradientMaxTemp}
                />
                <VisibleRangeOverlay
                  $visibleRange={visibleRange}
                  $minTemp={gradientMinTemp}
                  $maxTemp={gradientMaxTemp}
                />
              </SliderTrack>
              <ThumbEResize>
                <RangeHandleLeft style={{ cursor: 'e-resize' }} />
              </ThumbEResize>
              <ThumbWResize>
                <RangeHandleRight style={{ cursor: 'w-resize' }} />
              </ThumbWResize>
            </SliderRoot>
            {controlPoints.map((point, index) => {
              const position =
                ((point.temp_f - gradientMinTemp) /
                  (gradientMaxTemp - gradientMinTemp)) *
                100
              const color = rgbaToHex(
                point.color_rgba[0],
                point.color_rgba[1],
                point.color_rgba[2]
              )
              return (
                <ControlPointIndicator
                  key={index}
                  $position={clamp(position, 0, 100)}
                  $color={color}>
                  <span>
                    {formatValueForLayer(point.temp_f, activeLayer.prefix)}
                  </span>
                  <ControlPointDeleteButton
                    onClick={() => removeControlPoint(index)}
                    disabled={controlPoints.length <= MIN_CONTROL_POINTS}>
                    ×
                  </ControlPointDeleteButton>
                </ControlPointIndicator>
              )
            })}
            <AddControlPointButton onClick={addControlPoint}>
              +
            </AddControlPointButton>
          </FilterSliderContainer>
          <HistogramSpacer />
          <ColorSliderContainer>
            <AbsoluteSliderRoot
              min={gradientMinTemp}
              max={gradientMaxTemp}
              value={controlPoints.map((point) =>
                clamp(point.temp_f, gradientMinTemp, gradientMaxTemp)
              )}
              step={1}
              onValueChange={(values) => {
                const newPoints = [...controlPoints]
                values.forEach((value, index) => {
                  if (newPoints[index]) {
                    newPoints[index].temp_f = value
                  }
                })
                setControlPoints(newPoints)
              }}>
              <SliderTrack />
              {controlPoints.map((point, index) => (
                <ColorThumb
                  key={index}
                  id={`control-point-${index}`}
                  $color={rgbaToHex(
                    point.color_rgba[0],
                    point.color_rgba[1],
                    point.color_rgba[2]
                  )}>
                  <ColorHandle />
                </ColorThumb>
              ))}
            </AbsoluteSliderRoot>
          </ColorSliderContainer>
        </RelativeContainer>
        <TemperatureRangeLabels>
          <span>{formatValueForLayer(decodeMinTemp, activeLayer.prefix)}</span>
          <span>{formatValueForLayer(decodeMaxTemp, activeLayer.prefix)}</span>
        </TemperatureRangeLabels>
      </HeaderContainer>

      <SliderContainer>
        <SliderLabel>Opacity</SliderLabel>
        <StyledSliderRoot
          value={[opacity]}
          min={0}
          max={1}
          step={OPACITY_STEP}
          onValueChange={([value]) => setOpacity(value)}>
          <StyledSliderTrack>
            <StyledSliderRange />
          </StyledSliderTrack>
          <StyledSliderThumb />
        </StyledSliderRoot>
        <OpacityInput
          value={opacity.toFixed(2)}
          onChange={(e) => setOpacity(parseFloat(e.target.value) || 0)}
          type='number'
          min={0}
          max={1}
          step={OPACITY_STEP}
        />
      </SliderContainer>

      <ButtonsRow>
        <TooltipWrapper
          label={
            gradientByProbeActive
              ? 'Cancel Gradient by Probe'
              : 'Gradient by Probe'
          }
          side='top'>
          <IconButton
            $active={gradientByProbeActive}
            onClick={handleGradientByProbe}
            disabled={showSaveUI}>
            <TargetIcon />
          </IconButton>
        </TooltipWrapper>

        <TooltipWrapper label='Reset to Default Gradient' side='top'>
          <IconButton
            onClick={handleResetGradient}
            disabled={gradientByProbeActive || showSaveUI}>
            <ResetIcon />
          </IconButton>
        </TooltipWrapper>

        {!showSaveUI && !gradientByProbeActive && (
          <TooltipWrapper label='Save as Custom Layer' side='top'>
            <SaveButton onClick={() => setShowSaveUI(true)}>
              <BookmarkIcon />
              <span>Save Layer</span>
            </SaveButton>
          </TooltipWrapper>
        )}
      </ButtonsRow>

      {gradientByProbeActive && (
        <ProbeStatusMessage>
          {probedTemperatures.length === 0
            ? 'Right-click to probe first temperature (10% of LUT)'
            : 'Left-click to place second point and probe temperature (90% of LUT)'}
        </ProbeStatusMessage>
      )}

      {showSaveUI && (
        <SaveLayerInputContainer>
          <SaveLayerInput
            type='text'
            placeholder='Enter layer name...'
            value={customLayerName}
            onChange={(e) => setCustomLayerName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && customLayerName.trim()) {
                handleSaveLayer()
              }
            }}
            autoFocus
          />
          <SaveLayerActions>
            <CancelButton
              onClick={() => {
                setShowSaveUI(false)
                setCustomLayerName('')
              }}>
              Cancel
            </CancelButton>
            <ConfirmSaveButton
              onClick={handleSaveLayer}
              disabled={!customLayerName.trim() || isSaving}>
              {isSaving ? 'Saving...' : 'Save Layer'}
            </ConfirmSaveButton>
          </SaveLayerActions>
        </SaveLayerInputContainer>
      )}
    </ViewSectionContainer>
  )
}
